// GUID/UUID pattern. Owner / createdBy fields sometimes come back as a
// bare object ID instead of a resolved email or display name; we treat
// those as missing so the UI doesn't surface raw GUIDs to end users.
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when `value` is a non-empty, non-GUID string safe to show in the UI.
 */
export function isDisplayableValue(value: string | null | undefined): value is string {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (GUID_PATTERN.test(trimmed)) return false;
  return true;
}

/**
 * Normalize an owner / createdBy / updatedBy value for inline display.
 * Returns the trimmed string when displayable, or an em-dash fallback.
 */
export function formatDisplayValue(value: string | null | undefined, fallback = '—'): string {
  if (!isDisplayableValue(value)) return fallback;
  return value.trim();
}

/**
 * Render an owner / createdBy value as a person-name. ARM only gives us
 * the principal identifier (UPN), so for an email-shaped value we strip
 * the domain to get a readable handle (e.g. `alice@contoso.com` -> `alice`).
 * Non-email strings, GUIDs, and missing values fall through to the
 * em-dash fallback already handled by formatDisplayValue.
 */
export function formatUserDisplayName(value: string | null | undefined, fallback = '—'): string {
  if (!isDisplayableValue(value)) return fallback;
  const display = value.trim();
  const atIndex = display.indexOf('@');
  return atIndex > 0 ? display.slice(0, atIndex) : display;
}

/**
 * Result of {@link formatSystemDataPrincipal}: a small discriminated record the UI
 * can render however it wants (e.g. a person chip for `user`, a service-identity
 * badge for `service`).
 */
export type SystemDataPrincipalDisplay =
  | { kind: 'none'; display: string; full: null; principalType: null }
  | {
      kind: 'user';
      display: string;
      full: string;
      principalType: 'User';
    }
  | {
      kind: 'service';
      display: string;
      full: string;
      principalType: 'Application' | 'ManagedIdentity' | 'Key' | 'Unknown';
    };

/**
 * Format a `systemData.createdBy` / `lastModifiedBy` value into a render-ready record,
 * branching on `createdByType` per ARM convention:
 *
 *   - `User`            -> person-style (UPN trimmed to handle, full UPN preserved)
 *   - `Application`     -> service identity (badged "Application")
 *   - `ManagedIdentity` -> service identity (badged "Managed identity")
 *   - `Key`             -> service identity (badged "Key")
 *   - missing / unknown type with a value -> falls back to person-style if email-shaped,
 *     otherwise service-style with `principalType: 'Unknown'`
 *   - empty / missing value -> `none`
 */
export function formatSystemDataPrincipal(
  value: string | null | undefined,
  type: string | null | undefined,
): SystemDataPrincipalDisplay {
  // Only treat truly empty / whitespace values as "none". Unlike formatDisplayValue,
  // bare GUIDs are legitimate identifiers here (e.g. Application / ManagedIdentity ids)
  // so we must NOT strip them.
  if (!value || value.trim().length === 0) {
    return { kind: 'none', display: '—', full: null, principalType: null };
  }
  const full = value.trim();
  const normalizedType = (type ?? '').trim();
  const looksLikeEmail = full.indexOf('@') > 0;

  if (normalizedType === 'User' || (!normalizedType && looksLikeEmail)) {
    const atIndex = full.indexOf('@');
    const display = atIndex > 0 ? full.slice(0, atIndex) : full;
    return { kind: 'user', display, full, principalType: 'User' };
  }

  let principalType: 'Application' | 'ManagedIdentity' | 'Key' | 'Unknown';
  switch (normalizedType) {
    case 'Application':
    case 'ManagedIdentity':
    case 'Key':
      principalType = normalizedType;
      break;
    default:
      principalType = 'Unknown';
  }

  return { kind: 'service', display: full, full, principalType };
}
